import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { usePageParams } from 'hooks/use-page-params';
import { useQuery, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/management/accesses';

export interface ICrmAccessControlData {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  role: {
    id: number;
    name: string;
  };
  supplier: {
    id: number;
    name: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ICrmAccessControlApiResponse {
  data: ICrmAccessControlData[];
  pageCount: number;
  total: number;
  page: number;
  limit: number;
}

type TData = ICrmAccessControlApiResponse;
type TError = AxiosError;
type Options = any;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmAccessControlApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort, supplierId, name, phoneNumber, email } = getPageParams();
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
      };

      if (sort) params.sort = sort;
      if (supplierId) params.supplierId = supplierId;
      if (name) params.name = name;
      if (phoneNumber) params.phoneNumber = phoneNumber;
      if (email) params.email = email;

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  const queryKey = [endpoint, page, limit, sort, supplierId, name, phoneNumber, email];

  return useQuery({
    queryKey,
    queryFn,
    enabled: isLoggedIn,
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    ...otherOptions,
  });
}
